/**
 * 비동기 연속의 범위(scope) — 화면·프로젝트·세션처럼 "연속이 속한 문맥"을 키 하나로 나타낸다.
 *
 * 늦게 끝난 요청이 새 문맥을 덮는 문제는 종류가 둘뿐이다(리뷰 #388 R7~R13 의 지적 전부):
 *  ① 문맥이 끝났다(키 변경·언마운트) — `claim()` 이 돌려주는 술어가 false
 *  ② 같은 문맥 안에서 더 새 요청이 시작됐다 — `lane(name).begin()` 토큰의 `isLatest()` 가 false
 * 레인은 "무엇끼리 경쟁하는가"를 이름으로 선언한다(load·add·speed…). `enqueue` 는 같은 레인의
 * 작업을 순서대로 보낸다(서버가 옛 목표를 마지막으로 받지 않게). 훅별 세대 ref·소유권 토큰·
 * 스토어 재조회 같은 변형은 이 하나로 대체한다.
 */
import { useLayoutEffect, useMemo, useRef } from 'react';

export interface LaneToken {
  /** 이 요청이 아직 유효한가 — 범위가 살아 있고, 같은 레인에 더 새 요청이 없다 */
  isLatest: () => boolean;
}

export interface AsyncLane {
  /** 새 요청 시작 — 이 레인의 최신 토큰 */
  begin: () => LaneToken;
  /** 새 요청을 시작하지 않고 지금 상태를 잡는다 — "그새 교체가 없었나" 판정용(비교 추가 등) */
  current: () => LaneToken;
  /** 앞 작업이 끝난 뒤 실행(직렬). 차례가 왔을 때 범위가 이미 끝났으면 실행하지 않고 undefined 로 정착 */
  enqueue: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
  /** 재진입 슬롯 — 이 범위에서 이 레인의 작업이 진행 중이면 null, 아니면 해제 함수. 범위가 끝나면 슬롯도
   *  새 범위 것이 된다: 옛 범위의 진행 중 작업이 새 범위를 막지 않고, 옛 해제가 새 슬롯을 풀지 않는다 */
  tryAcquire: () => (() => void) | null;
}

export interface AsyncScope {
  /** 문맥 소유권 — 범위가 살아 있는 동안만 true 인 술어 */
  claim: () => () => boolean;
  lane: (name: string) => AsyncLane;
}

export interface AsyncScopeCore extends AsyncScope {
  /** 범위 종료(키 변경·언마운트) — 이후 새 요청은 새 범위에 속한다 */
  end: () => void;
}

/** 순수 코어 — 훅 밖(테스트·비 React 호출자)에서도 쓴다 */
export function createAsyncScope(): AsyncScopeCore {
  let generation = 0;
  const lanes = new Map<string, { counter: number; chain: Promise<unknown>; heldBy: number | null; api?: AsyncLane }>();
  const laneOf = (name: string) => {
    let l = lanes.get(name);
    if (!l) { l = { counter: 0, chain: Promise.resolve(), heldBy: null }; lanes.set(name, l); }
    return l;
  };
  const tokenFor = (l: { counter: number }, counter: number, gen: number): LaneToken => ({
    isLatest: () => gen === generation && counter === l.counter,
  });
  // 레인 객체는 이름당 하나(안정 참조) — 훅 deps 에 넣어도 렌더마다 바뀌지 않는다
  const lane = (name: string): AsyncLane => {
    const l = laneOf(name);
    if (!l.api) {
      l.api = {
        begin: () => tokenFor(l, ++l.counter, generation),
        current: () => tokenFor(l, l.counter, generation),
        enqueue: <T,>(fn: () => Promise<T>) => {
          const gen = generation;
          const guarded = () => (gen === generation ? fn() : Promise.resolve(undefined));
          const run = l.chain.then(guarded, guarded);
          l.chain = run.catch(() => undefined);
          return run;
        },
        tryAcquire: () => {
          if (l.heldBy === generation) return null;
          const gen = generation;
          l.heldBy = gen;
          return () => { if (l.heldBy === gen) l.heldBy = null; };
        },
      };
    }
    return l.api;
  };
  return {
    claim: () => { const gen = generation; return () => gen === generation; },
    lane,
    // 범위 종료: 토큰 무효화 + 레인 체인·슬롯 초기화 — 옛 범위의 미해결 작업이 새 범위를 막거나 그 뒤에 줄 세우지 않는다
    end: () => { generation++; for (const l of lanes.values()) { l.chain = Promise.resolve(); l.heldBy = null; } },
  };
}

/** 키가 바뀌거나 언마운트되면 범위가 끝난다. 반환 객체는 마운트 동안 동일(deps 안전).
 *  종료는 **커밋 단계**(layout effect 정리)에서 — 패시브 효과 정리로 미루면 키가 바뀐 렌더가 커밋된 뒤
 *  패시브 효과 전에 도는 마이크로태스크(늦은 응답)가 아직 유효한 토큰으로 새 문맥에 쓴다 */
export function useAsyncScope(key: string | null | undefined): AsyncScope {
  const coreRef = useRef<AsyncScopeCore | null>(null);
  if (!coreRef.current) coreRef.current = createAsyncScope();
  useLayoutEffect(() => () => { coreRef.current?.end(); }, [key]);
  return useMemo(() => ({ claim: () => coreRef.current!.claim(), lane: (name: string) => coreRef.current!.lane(name) }), []);
}
